import { useEffect, useRef } from "react";
import { Toast } from "primereact/toast";
import { Link, useLocation, useNavigate } from "react-router-dom";
import DeviceInfo from "../../ComponentesGlobales/DeviceInfo";
import AppToast from "../../ComponentesGlobales/AppToast";
import { obtenerGruposPermitidos } from "../../config/navegacion";
import { obtenerSesion } from "../../servicios/sesionSeguridad";
import { useStoreLogin } from "../Login/useStoreLogin";
import { useStoreHome } from "./useStoreHome";
import "./home.css";

function Home() {
  const toast = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const Caducidad = useStoreHome((state) => state.Caducidad);
  const FechaCaducidad = useStoreHome((state) => state.FechaCaducidad);
  const deviceConsentimiento = useStoreHome((state) => state.deviceConsentimiento);
  const setDeviceConsentimiento = useStoreHome((state) => state.setDeviceConsentimiento);
  const setDeviceInfo = useStoreHome((state) => state.setDeviceInfo);
  const grupos = obtenerGruposPermitidos();

  useEffect(() => {
    if (!Caducidad) return;
    toast.current?.clear();
    const vencida = !FechaCaducidad || FechaCaducidad < new Date();
    const mensaje = vencida ? Caducidad.replace("finaliza", "finalizó") : Caducidad;
    toast.current?.show({
      severity: vencida ? "error" : "success",
      sticky: true,
      content: () => (
        <AppToast
          titulo={vencida ? "Período de prueba finalizado" : "Período de prueba"}
          mensaje={mensaje}
          severity={vencida ? "error" : "success"}
          icono={vencida ? "pi-exclamation-circle" : "pi-calendar"}
        />
      ),
    });
    useStoreHome.setState({ Caducidad: "" });
  }, [Caducidad, FechaCaducidad]);

  useEffect(() => {
    useStoreHome.getState().obtenerCaducidad();
  }, []);

  useEffect(() => {
    if (!location.state?.suscripcionVencida) return;
    toast.current?.clear();
    toast.current?.show({
      severity: "error",
      sticky: true,
      content: () => (
        <AppToast
          titulo="Suscripción vencida"
          mensaje="La suscripción caducó. Contactá a soporte técnico para renovarla."
          severity="error"
          icono="pi-exclamation-circle"
        />
      ),
    });
    navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, location.state?.suscripcionVencida, navigate]);

  const fecha = new Date().toLocaleDateString("es-AR", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
  const nombre = localStorage.getItem("Nombre") || "";
  const empresa = obtenerSesion()?.sDescripcion || localStorage.getItem("Descripcion") || "";

  const bloquearSiNoPermite = (event) => {
    if (deviceConsentimiento === "granted") return;
    const link = event.target.closest("a");
    if (!link) return;
    event.preventDefault();
    event.stopPropagation();
    toast.current?.show({
      severity: "warn",
      summary: "Primero habilitá los permisos",
      detail: "Necesitamos ubicación y cámara para registrar movimientos de forma segura.",
      life: 4000,
    });
  };

  return (
    <div className="home-container">
      <Toast ref={toast} position="bottom-right" className="app-toast" />

      <header className="home-topbar">
        <div>
          <span className="home-empresa">{empresa}</span>
          <span className="home-fecha">{fecha.charAt(0).toUpperCase() + fecha.slice(1)}</span>
        </div>
        <Link
          to="/tutorial/panel-principal"
          target="_blank"
          rel="noopener noreferrer"
          className="home-ayuda"
          aria-label="Abrir el instructivo del panel principal en una pestaña nueva"
        >
          <i className="pi pi-book" aria-hidden="true" />
          <span>Instructivo</span>
        </Link>
      </header>

      <main className="home">
        <section className="home-bienvenida">
          <span className="home-eyebrow">Panel principal</span>
          <h1>{nombre ? `Hola, ${nombre}` : "Hola"}</h1>
          <p>¿Qué necesitás hacer hoy?</p>
        </section>

        {deviceConsentimiento === null && (
          <section className="permiso-aviso permiso-aviso-pendiente" aria-labelledby="permiso-titulo">
            <i className="pi pi-map-marker" aria-hidden="true" />
            <div className="permiso-copy">
              <strong id="permiso-titulo">Habilitá ubicación y cámara</strong>
              <span>Se usan al escanear códigos y registrar movimientos. Podés cambiar este permiso después.</span>
            </div>
            <div className="permiso-acciones">
              <button type="button" className="btn-secundario" onClick={() => setDeviceConsentimiento("denied")}>Ahora no</button>
              <button type="button" className="btn-primario" onClick={() => setDeviceConsentimiento("granted")}>Habilitar</button>
            </div>
          </section>
        )}

        {deviceConsentimiento === "denied" && (
          <section className="permiso-aviso permiso-aviso-denegado" role="status">
            <i className="pi pi-exclamation-circle" aria-hidden="true" />
            <div className="permiso-copy">
              <strong>Algunas funciones están pausadas</strong>
              <span>Habilitá ubicación y cámara para usar las opciones del sistema.</span>
            </div>
            <button type="button" className="btn-alerta" onClick={() => useStoreHome.getState().resetConsentimiento()}>Reintentar</button>
          </section>
        )}

        {deviceConsentimiento === "granted" && (
          <div className="device-info-oculto" aria-hidden="true">
            <DeviceInfo onInfoUpdate={(info) => {
              setDeviceInfo(info);
              useStoreLogin.getState().bitacoraOperadores();
            }} />
          </div>
        )}

        <section className={`home-secciones ${deviceConsentimiento !== "granted" ? "acciones-bloqueadas" : ""}`} onClickCapture={bloquearSiNoPermite} aria-labelledby="home-acciones">
          <div className="home-grupo-titulo">
            <div>
              <h2 id="home-acciones">Accesos principales</h2>
              <p>Las tareas de todos los días, en un solo lugar.</p>
            </div>
          </div>
          <div className="home-grupos">
            {grupos.map((grupo) => (
              <Link className="home-area-card" key={grupo.id} to={grupo.ruta}>
                <span className="home-area-icono" aria-hidden="true"><i className={`pi ${grupo.icono}`} /></span>
                <span className="home-area-copy">
                  <strong>{grupo.titulo}</strong>
                  <span>{grupo.descripcion}</span>
                  <small>{grupo.opciones.length} {grupo.opciones.length === 1 ? "opción" : "opciones"}</small>
                </span>
                <i className="pi pi-arrow-right home-area-flecha" aria-hidden="true" />
              </Link>
            ))}
          </div>
          <p className="home-configuracion-ayuda"><i className="pi pi-info-circle" aria-hidden="true" /> Los accesos disponibles dependen de tus permisos.</p>
        </section>
      </main>

    </div>
  );
}

export default Home;
